import { Form, FormRenderer } from "../MDDForm/MDDForm.jsx";
import { fdefFormX } from "formdefs/formX.js";
import { fdefFormA } from "formdefs/formA.js";
import { fdefFormB } from "formdefs/formB.js";
import { fdefFormC } from "formdefs/formC.js";
import { fdefFormD } from "formdefs/formD.js";
import { fdefFormE } from "formdefs/formE.js";
import { fdefFormF } from "formdefs/formF.js";
import { fdefFormG } from "formdefs/formG.js";
import { fdefFormH } from "formdefs/formH.js";
import { fdefFormI } from "formdefs/formI.js";
import { fdefFormSonstige } from "formdefs/formSonstige.js";
import { fdefFormHausrat } from "formdefs/formHausrat.js";
import { useState, useCallback, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import Spinner from "components/Spinner.jsx";
import { disableControls, cloneDeep } from "../MDDForm/helper.js";
import { useSnackbar } from "notistack";
import { useRecoilState } from "recoil";
import { backendDataAtom } from "recoil/atoms/backendDataAtom.js";
import { useNavigate } from "react-router-dom";
import { flushSync } from "react-dom";
import {
  backendGet,
  backendPost,
  backendPatch,
} from "../../services/dataService.js";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard.js";
import UnsavedChangesModal from "../UnsavedChangesModal.jsx";
import { activeBrand } from "../../theme/brands/index.js";
import { applyJourneyToFdef } from "formdefs/journey/buildJourneySteps.js";
import { applyClaimWizardToFdef } from "formdefs/claimWizard/buildClaimWizardSteps.js";
import { formKeyOf } from "formdefs/claimWizard/familyMap.js";
import { useUser } from "../../hooks/user.js";
import { userHasRole } from "../../utils/policyUtils.js";

function SchadenFormComponent({ model, hideTitle, sparte, disable, claimStatus }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [suppressUnsavedChangesGuard, setSuppressUnsavedChangesGuard] =
    useState(false);
  const is_mounted_ref = useRef(true);
  const { enqueueSnackbar } = useSnackbar();
  const [backendData, setBackendData] = useRecoilState(backendDataAtom);
  const navigate = useNavigate();

  const {
    showModal: showUnsavedChangesModal,
    confirmNavigation,
    cancelNavigation,
  } = useUnsavedChangesGuard(isDirty && !suppressUnsavedChangesGuard);

  useEffect(() => {
    return () => {
      is_mounted_ref.current = false;
    };
  }, []);

  useEffect(() => {
    setSuppressUnsavedChangesGuard(false);
  }, [model?.id]);

  const handleDirtyChange = useCallback((dirty) => {
    setIsDirty(dirty);
    // console.log("dirty?", dirty);
  }, []);

  /**
   * Navigate to a new route without showing the unsaved changes guard.
   * Since it deliberately bypasses the unsaved changes guard, it must
   * only be used for navigating after a successful save.
   */
  const navigateWithoutUnsavedChangesGuard = useCallback(
    (to) => {
      flushSync(() => {
        setIsDirty(false);
        setSuppressUnsavedChangesGuard(true);
      });

      navigate(to);

      queueMicrotask(() => {
        if (is_mounted_ref.current) {
          setSuppressUnsavedChangesGuard(false);
        }
      });
    },
    [navigate]
  );

  const schadenGespeichert = (data) => {
    enqueueSnackbar(
      "Schaden wurde gespeichert - ACHTUNG: damit ist er noch nicht zur Prüfung gemeldet!",
      { variant: "success" }
    );
    setBackendData({ data: data, id: data.id, type: "schaden" });
    navigateWithoutUnsavedChangesGuard(
      "/schaeden/" + data.id + "?tab=schadenformular"
    );
  };

  const schadenGemeldet = (data) => {
    enqueueSnackbar("Schaden wurde zur Prüfung gemeldet", {
      variant: "success",
    });
    setBackendData({ data: data, id: data.id, type: "schaden" });
    navigateWithoutUnsavedChangesGuard(
      "/schaeden/" + data.id + "?tab=schadenformular&dialog=pre_check"
    );
  };

  // Sparte → formdef is resolved via the shared familyMap table (single source of
  // truth also used by the schmidt wizard builder).
  const FORMDEFS = {
    A: fdefFormA,
    B: fdefFormB,
    C: fdefFormC,
    D: fdefFormD,
    E: fdefFormE,
    F: fdefFormF,
    G: fdefFormG,
    H: fdefFormH,
    I: fdefFormI,
    Sonstige: fdefFormSonstige,
    Hausrat: fdefFormHausrat,
    X: fdefFormX,
  };

  const getFormDefinition = (sparte) => {
    // `formKeyOf` returns "X" for a Sparte that familyMap does not map, and
    // `fdefFormX` is an EMPTY stepper — so the wizard has no descriptors to pull
    // and its steps render blank. 57 of the Sparten present in the data are
    // unmapped, so fall back to the general "Sonstige Gefahren" form: a complete
    // form the broker can still re-classify via the Einordnung step.
    // NOTE stopgap — the real fix is mapping those Sparten in familyMap.js.
    const key = formKeyOf(sparte);
    const fdef = cloneDeep(FORMDEFS[key === "X" ? "Sonstige" : key] || fdefFormSonstige);

    if (disable) fdef.formdefinition = disableControls(fdef.formdefinition);
    return fdef;
  };

  const submitCallback = async (formData) => {
    const id = model?.id;
    if (!id) {
      return await backendPost("data/schadens", {
        toSend: formData,
        setIsLoading,
      });
    } else {
      return await backendPatch(`data/schadens/${id}/formdata`, {
        toSend: formData,
        setIsLoading,
      });
    }
  };

  // Skin-gated wizard presentation: 'broker' (generic) uses the click-dummy
  // journey top-stepper; 'customer' (schmidt) uses the two-phase PhaseRail.
  const { user: currentUser } = useUser();
  const claimWizardFlow = activeBrand.features?.claimWizardFlow ?? "broker";
  const stepperVariant =
    claimWizardFlow === "customer"
      ? "phaserail"
      : claimWizardFlow === "broker"
        ? "journey"
        : null;
  const isBroker = userHasRole(currentUser, [
    "sysadmin",
    "makler_sachbearbeitung",
    "makler_buchhaltung",
  ]);

  // The active Sparte can change when the broker re-classifies mid-flow; keep it
  // in state (resynced from the prop) so the schmidt wizard re-derives its family
  // step list and the renderer re-mounts (via the key below).
  const [sparteState, setSparteState] = useState(sparte);
  useEffect(() => {
    setSparteState(sparte);
  }, [sparte]);

  // schmidt (phaserail): curated customer phase + family-specific broker phase.
  // generic (journey): the click-dummy single-phase journey. Both re-group fields
  // by name only, so validation/submit are unchanged.
  const resolveFdef = () => {
    const fdef = getFormDefinition(sparteState);
    if (stepperVariant === "phaserail")
      return applyClaimWizardToFdef(fdef, sparteState);
    if (stepperVariant === "journey") return applyJourneyToFdef(fdef);
    return fdef;
  };

  // Two-phase (PhaseRail) submit callbacks. formdata carries `status` +
  // `customer_locked_fields` via the action's additionalData.
  const handoffCallback = async (formdata) => {
    const data = await submitCallback(formdata);
    if (!data) return;
    await backendPatch(`data/schadens/${data.id}/status`, {
      toSend: {
        status: "awaiting_broker_completion",
        customer_locked_fields: formdata.customer_locked_fields || [],
      },
      setIsLoading,
      setResult: (updated) => {
        enqueueSnackbar("Ihre Schadenmeldung wurde an den Makler übergeben.", {
          variant: "success",
        });
        setBackendData({ data: updated, id: updated.id, type: "schaden" });
        navigateWithoutUnsavedChangesGuard(
          "/schaeden/" + updated.id + "?tab=schadenformular"
        );
      },
    });
  };

  const brokerSubmitCallback = async (formdata) => {
    const data = await submitCallback(formdata);
    if (!data) return;
    await backendPatch(`data/schadens/${data.id}/status`, {
      toSend: { status: "processing" },
      setIsLoading,
      setResult: (updated) => {
        enqueueSnackbar("Der Schaden wurde an den Versicherer gesendet.", {
          variant: "success",
        });
        setBackendData({ data: updated, id: updated.id, type: "schaden" });
        navigateWithoutUnsavedChangesGuard(
          "/schaeden/" + updated.id + "?tab=schadenformular"
        );
      },
    });
  };

  // Broker re-classification (schmidt Einordnung step): persist the current draft,
  // then change the claim's Sparte via the dedicated endpoint (which also rewrites
  // the PDF template). On success, switch sparteState so the broker step list
  // re-derives for the new family and the renderer re-mounts.
  //
  // Deliberately runs WITHOUT `setIsLoading`: that would blank the whole wizard
  // with the full-page <Spinner/> (a jarring "reload" flash). The re-derived
  // step list re-mounts quietly; the snackbar confirms the change.
  const noopLoading = () => {};
  const reclassifyCallback = async (formdata) => {
    const id = model?.id;
    const newSparte = formdata.sparte;
    if (!id || !newSparte || newSparte === sparteState) return;
    const data = await backendPatch(`data/schadens/${id}/formdata`, {
      toSend: formdata,
      setIsLoading: noopLoading,
    });
    if (!data) return;
    await backendPatch(`data/schadens/${id}/reclassify`, {
      toSend: { sparte: newSparte, schaden_art: formdata.schaden_art },
      setIsLoading: noopLoading,
      setResult: (updated) => {
        enqueueSnackbar("Die Sparte wurde geändert.", { variant: "success" });
        setBackendData({ data: updated, id: updated.id, type: "schaden" });
        setSparteState(newSparte);
      },
    });
  };

  const formContent = (
    <Form>
      <FormRenderer
        key={`${model?.id ?? "new"}:${sparteState}`}
        fdef={resolveFdef()}
        model={model}
        variant="filled"
        editMode={true}
        showActions={true && !disable}
        showCancel={false}
        showToggleEdit={false}
        apiGet={backendGet}
        actionsPosition="both"
        toggleEditPosition="top"
        showErrorText={true}
        useV2Layout={true}
        stepperVariant={stepperVariant}
        claimStatus={claimStatus ?? model?.status?.value}
        currentUserIsBroker={isBroker}
        handoffCallback={handoffCallback}
        brokerSubmitCallback={brokerSubmitCallback}
        reclassifyCallback={reclassifyCallback}
        delegateActions={true}
        onDirtyChange={handleDirtyChange}
        submitAction={{
          method: "callback",
          callback: async (formdata) => {
            const data = await submitCallback(formdata);
            if (!data) return;
            schadenGespeichert(data);
          },
          alwaysActive: true,
        }}
        submitAction2={{
          method: "callback",
          callback: async (formdata) => {
            const data = await submitCallback(formdata);
            if (!data) {
              return;
            }

            // Update status and use the fresh data with new status
            await backendPatch(`data/schadens/${data.id}/status`, {
              toSend: { status: "pre_check" },
              setIsLoading,
              setResult: (updatedData) => {
                schadenGemeldet(updatedData);
              },
            });
          },
          label: "zur Prüfung melden",
          activeWhen: (isFormValid) => {
            return (
              isFormValid &&
              !["pre_check", "customer_pre_check"].includes(
                model?.status?.value
              )
            );
          },
        }}
        stepperProgressMode={model?.id ? "full" : "lax"}
      />
    </Form>
  );

  if (isLoading) return <Spinner />;

  if (hideTitle) {
    return (
      <>
        {formContent}
        <UnsavedChangesModal
          open={showUnsavedChangesModal}
          onConfirm={confirmNavigation}
          onCancel={cancelNavigation}
        />
      </>
    );
  } else {
    return (
      <Box sx={{ textAlign: "center" }}>
        <Typography variant="h3">Schadenformular</Typography>
        {formContent}
        <UnsavedChangesModal
          open={showUnsavedChangesModal}
          onConfirm={confirmNavigation}
          onCancel={cancelNavigation}
        />
      </Box>
    );
  }
}

export default SchadenFormComponent;
